import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { GlobalService } from "@frontend/app/serivces/global.service";
import { MasterService } from "@frontend/app/serivces/master.service";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { environment } from "@frontend/environments/environment";
import { SVG_ICONS } from "@frontend/app/models/icons";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { Router } from "@angular/router";
import { ToastrService } from "@frontend/app/serivces/toastr.service";

@Component({
  selector: "app-add-order",
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatAutocompleteModule, MatInputModule, MatFormFieldModule, SharedModule],
  templateUrl: "./add-order.component.html",
  styleUrl: "./add-order.component.scss",
})
export class AddOrderComponent implements OnInit, OnDestroy {
  private globalService = inject(GlobalService);
  private masterService = inject(MasterService);
  private router = inject(Router);
  private toastrService = inject(ToastrService);

  ngOnInit(): void {
    this.globalService.setIsShowFalse();
    this.loadStudents();
    this.loadProducts();
    this.loadTrainerDetails();
  }

  loadTrainerDetails() {
    const trainerId = Number(sessionStorage.getItem("TrainerID") || 0);
    if (trainerId) {
      this.masterService.getCoachDetailsByID(trainerId).subscribe({
        next: (res: any) => {
          if (res && res.Table) {
            this.trainerDetails = res.Table;
          }
        },
        error: err => console.error("Error loading trainer details", err),
      });
    }
  }

  //#region Variables
  currentStep = 1;
  trainerDetails: any = null;
  students: any[] = [];

  // Initialize with default values for selection
  kitItems: any[] = [];

  cart: any[] = [];
  notes = "";

  get totalAmount(): number {
    return this.cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }
  readonly SVG_ICONS = SVG_ICONS;

  get cartSummary() {
    const summary: any[] = [];
    this.cart.forEach(item => {
      const existing = summary.find(s => s.name === item.name && s.size === item.size);
      if (existing) {
        existing.quantity += item.quantity;
        existing.totalPrice += item.price * item.quantity;
      } else {
        summary.push({
          name: item.name,
          size: item.size,
          price: item.price,
          quantity: item.quantity,
          totalPrice: item.price * item.quantity,
          image: item.image,
        });
      }
    });
    return summary;
  }
  //#endregion

  removeGroup(name: string, size: string) {
    this.cart = this.cart.filter(item => !(item.name === name && item.size === size));
  }

  loadStudents() {
    const centerId = sessionStorage.getItem("AllCenterIDs") || "";
    this.masterService.getStudentList(1, 1000, "FirstName asc", centerId, "", "", "").subscribe({
      next: (res: any) => {
        if (res && res.Table) {
          this.students = res.Table;
        }
      },
      error: err => console.error("Error loading students", err),
    });
  }

  loadProducts() {
    this.masterService.getProductList(1, 1000, "ProductID asc").subscribe({
      next: (res: any) => {
        if (res && res.data) {
          this.kitItems = res.data.map((product: any) => {
            // Extract sizes and create mapping
            const sizeMapping: { [key: string]: { id: number; stock: number } } = {};
            const sizes: string[] = [];

            if (product.ProductSizeInventory && product.ProductSizeInventory.length > 0) {
              product.ProductSizeInventory.forEach((inv: any) => {
                if (inv.InventorySize && inv.InventorySize.Size) {
                  const sizeName = inv.InventorySize.Size;
                  sizes.push(sizeName);
                  sizeMapping[sizeName] = {
                    id: inv.SizeID,
                    stock: Number(inv.CurrentStock) || 0,
                  };
                }
              });
            } else {
              const defaultSize = "One Size";
              sizes.push(defaultSize);
              sizeMapping[defaultSize] = {
                id: 0,
                stock: Number(product.CurrentStock) || 0,
              };
            }

            const uniqueSizes = [...new Set(sizes)];

            // Extract image from Documents
            // Extract image from Documents or Image property
            let imageUrl = "assets/images/placeholder.png";

            // Check Documents first
            if (product.Documents && product.Documents.length > 0) {
              // Find the first document with FileType 'Image' and a valid URL
              const imageDoc = product.Documents.find((doc: any) => doc.FileType === "Image" || (doc.FileType === "IMAGE" && doc.URL));
              if (imageDoc) {
                imageUrl = imageDoc.URL;
              }
            }

            // Fallback to Image property if no Document image found
            if (imageUrl === "assets/images/placeholder.png" && product.Image) {
              imageUrl = `${environment.img}${product.Image}`;
            }

            return {
              name: product.ProductName,
              price: product.Amount || 0, // Assuming Amount is the price
              selectedSize: "",
              selectedSizeStock: 0,
              quantity: 1,
              sizes: uniqueSizes.length > 0 ? uniqueSizes : ["One Size"],
              image: imageUrl,
              // Store original product ID if needed for backend
              productId: product.ProductID,
              sizeMapping: sizeMapping,
            };
          });
        }
      },
      error: err => console.error("Error loading products", err),
    });
  }

  // Product Selection Logic
  selectSize(item: any, size: string) {
    item.selectedSize = size;
    item.selectedSizeStock = item.sizeMapping ? item.sizeMapping[size]?.stock : 0;
    item.quantity = 1;
  }

  incrementQuantity(item: any) {
    if (item.quantity < item.selectedSizeStock) {
      item.quantity++;
    }
  }

  decrementQuantity(item: any) {
    if (item.quantity > 1) {
      item.quantity--;
    }
  }

  addToCart(item: any) {
    if (!item.selectedSize) return;

    // Add separate entries for each quantity
    for (let i = 0; i < item.quantity; i++) {
      this.cart.push({
        name: item.name,
        price: item.price,
        size: item.selectedSize,
        quantity: 1,
        studentId: null,
        image: item.image,
        filteredStudents: [...this.students],
        searchText: "",
        productId: item.productId,
        sizeId: item.sizeMapping ? item.sizeMapping[item.selectedSize]?.id : 0,
      });
    }

    // Reset selection
    item.selectedSize = "";
    item.selectedSizeStock = 0;
    item.quantity = 1;
  }

  // Cart Logic
  removeFromCart(index: number) {
    this.cart.splice(index, 1);
  }

  // Search Logic
  filterStudents(item: any) {
    const searchText = item.searchText;
    if (typeof searchText !== "string") {
      return;
    }
    const filterValue = searchText.toLowerCase();
    item.filteredStudents = this.students.filter(student => student.FirstName.toLowerCase().includes(filterValue) || student.LastName.toLowerCase().includes(filterValue));
  }

  onStudentSelected(item: any, event: any) {
    const selectedStudent = event.option.value;
    item.studentId = selectedStudent.StudentID;
  }

  displayStudent(student: any): string {
    if (typeof student === "string") return student;
    return student ? `${student.FirstName} ${student.MiddleName} ${student.LastName}` : "";
  }

  // Navigation
  nextStep() {
    if (this.cart.length > 0) {
      this.currentStep = 2;
    }
  }

  prevStep() {
    this.currentStep = 1;
  }

  getStudentName(id: number): string {
    const student = this.students.find(s => s.StudentID === id);
    return student ? `${student.FirstName} ${student.MiddleName} ${student.LastName}` : "";
  }

  showConfirmation = false;

  submitOrder() {
    // Validate that all items have a student assigned
    const unassignedItems = this.cart.filter(item => !item.studentId);
    if (unassignedItems.length > 0) {
      alert("Please assign a student to all items.");
      return;
    }

    this.showConfirmation = true;
  }

  cancelSubmit() {
    this.showConfirmation = false;
  }

  confirmSubmit() {
    const centerId = Number(sessionStorage.getItem("selectedCenter") || 0);
    const trainerId = Number(sessionStorage.getItem("TrainerID") || 0); // Assuming TrainerID is stored in session

    const payload = {
      CenterID: this.trainerDetails?.CenterID || 1,
      TrainerID: trainerId,
      ClientName: "", // Optional or derived
      MobileNo: this.trainerDetails?.MobileNumber || "", // Optional or derived
      Quantity: this.cart.length,
      SGST: 0,
      CGST: 0,
      Discount: 0,
      Product: this.cart.map(item => ({
        ProductID: item.productId,
        Quantity: 1,
        SizeID: item.sizeId || 0,
        StudentID: item.studentId,
        ClientName: "",
        MobileNo: "",
        ReferenceBy: "",
      })),
      AdditionalDiscount: 0,
      TotalAmount: this.totalAmount,
      IsActive: true,
    };

    // Call API to create order
    this.masterService.addOrder(payload).subscribe({
      next: (res: any) => {
        this.showConfirmation = false;
        this.router.navigate(["/orders"]);
        this.toastrService.success("Order created successfully");
      },
      error: err => console.error("Error creating order", err),
    });
  }

  ngOnDestroy() {
    this.globalService.setIsShowTrue();
  }
}
